import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { nurseBandingApi, type NurseBandYearHistoryRow } from '../services/api';
import type { Position } from '../types';
import { useToast } from './Toast';

interface NurseBandYearHistoryPanelProps {
  employeeId: number;
  /** Main + sub positions for this employee — each can have its own BY timeline. */
  positions: Position[];
  onSaved?: () => void | Promise<void>;
}

const NurseBandYearHistoryPanel: React.FC<NurseBandYearHistoryPanelProps> = ({
  employeeId,
  positions,
  onSaved,
}) => {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<NurseBandYearHistoryRow[]>([]);

  const defaultPositionId = positions[0]?.id ?? 0;

  const load = async (showSpinner = true) => {
    try {
      if (showSpinner) setLoading(true);
      const data = await nurseBandingApi.getBandYearHistory(employeeId);
      setRows(data);
    } catch (err: any) {
      toastRef.current.error(err?.response?.data?.message || err?.message || 'Failed to load band year history.');
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [employeeId]);

  const handleSave = async () => {
    if (rows.some((r) => !r.positionId || r.positionId <= 0)) {
      toast.error('Select a position for each band year step.');
      return;
    }
    try {
      setSaving(true);
      await nurseBandingApi.saveBandYearHistory(employeeId, rows);
      toast.success('Band year history saved.');
      await load(false);
      await onSaved?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center text-sm text-neutral-500 py-4">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading band year history…
      </div>
    );
  }

  return (
    <div className="mt-6 border-t border-neutral-200 pt-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-semibold text-neutral-900">Position band year (BY) history</h4>
          <p className="text-sm text-neutral-600">
            Effective-dated band year per position. When someone works two roles, each position uses its own BY
            against that position&apos;s rate table.
          </p>
        </div>
        <button
          type="button"
          disabled={positions.length === 0}
          onClick={() =>
            setRows((r) => [
              ...r,
              {
                positionId: defaultPositionId,
                effectiveFrom: new Date().toISOString().slice(0, 10),
                bandYear: 0,
              },
            ])
          }
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-40"
        >
          <Plus className="h-4 w-4" /> Add step
        </button>
      </div>
      {positions.length === 0 ? (
        <p className="text-sm text-amber-700 mb-3">
          Assign a main or sub position on the employee profile before configuring band years.
        </p>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-sm text-neutral-500 mb-3">
          No band year history — position banding payroll will not run for this employee.
        </p>
      ) : (
        <div className="space-y-2 mb-4">
          {rows.map((row, index) => (
            <div key={index} className="flex flex-wrap items-center gap-2">
              <label className="text-sm text-neutral-600">Position</label>
              <select
                className="rounded border border-neutral-300 px-2 py-1.5 text-sm min-w-[10rem]"
                value={row.positionId || ''}
                onChange={(e) => {
                  const positionId = parseInt(e.target.value, 10);
                  setRows((prev) => {
                    const next = [...prev];
                    next[index] = { ...next[index], positionId };
                    return next;
                  });
                }}
              >
                <option value="" disabled>
                  Select position
                </option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
                {row.positionId > 0 && !positions.some((p) => p.id === row.positionId) ? (
                  <option value={row.positionId}>{row.positionName || `Position ${row.positionId}`}</option>
                ) : null}
              </select>
              <label className="text-sm text-neutral-600 ml-2">Effective from</label>
              <input
                type="date"
                className="rounded border border-neutral-300 px-2 py-1.5 text-sm"
                value={row.effectiveFrom?.slice(0, 10) ?? ''}
                onChange={(e) => {
                  setRows((prev) => {
                    const next = [...prev];
                    next[index] = { ...next[index], effectiveFrom: e.target.value };
                    return next;
                  });
                }}
              />
              <label className="text-sm text-neutral-600 ml-2">Year</label>
              <select
                className="rounded border border-neutral-300 px-2 py-1.5 text-sm"
                value={row.bandYear}
                onChange={(e) => {
                  setRows((prev) => {
                    const next = [...prev];
                    next[index] = { ...next[index], bandYear: parseInt(e.target.value, 10) };
                    return next;
                  });
                }}
              >
                {Array.from({ length: 7 }, (_, i) => (
                  <option key={i} value={i}>
                    Year {i}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                className="text-red-600 p-1"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        disabled={saving || positions.length === 0}
        onClick={handleSave}
        className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save band year history'}
      </button>
    </div>
  );
};

export default NurseBandYearHistoryPanel;
